/**
 * HealthScore — Subscription health dial for the Overview tab.
 *
 * Computes a 0-100 score from data already returned by
 * /v1/subscriptions/overview (no new API call).
 *
 * Score breakdown (4 factors, 100 pts total):
 *   Payment Health    0–40  active=40, trialing=28, free=15, past_due=5, canceled=0
 *   Usage Headroom    0–30  full points when all metrics < 50%; drops to 0 at 100%
 *   Billing Commitment 0–20 active+no-cancel=20, active+cancel=10, trialing=8, else=0
 *   Plan Tier          0–10 enterprise=10, pro=7, free=3
 */

import React, { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import type { SubscriptionOverview } from "../../api/subscriptions";

// ── Score computation ─────────────────────────────────────────────────────────

interface ScoreFactor {
  name: string;
  score: number;
  max: number;
  description: string;
  color: string;
}

interface HealthResult {
  total: number;
  label: string;
  ringColor: string;
  textColor: string;
  bgColor: string;
  factors: ScoreFactor[];
}

function computeHealth(ov: SubscriptionOverview): HealthResult {
  const { subscription, usage, plan } = ov;
  const status = subscription.status;

  // Factor 1 — Payment Health (0-40)
  const paymentScore =
    status === "active"   ? 40 :
    status === "trialing" ? 28 :
    plan  !== "free"      ? 15 :  // free plan, no sub
    status === "past_due" ?  5 : 0;

  // Factor 2 — Usage Headroom (0-30)
  // Largest usage% across all 3 metrics drives the penalty
  const maxPct = Math.max(
    usage.repos.limit === -1       ? 0 : (usage.repos.current / Math.max(usage.repos.limit, 1)) * 100,
    usage.members.limit === -1     ? 0 : (usage.members.current / Math.max(usage.members.limit, 1)) * 100,
    usage.scansThisMonth.limit === -1 ? 0 : (usage.scansThisMonth.current / Math.max(usage.scansThisMonth.limit, 1)) * 100,
  );
  const usageScore = maxPct >= 100 ? 0 : maxPct <= 50 ? 30 : Math.round(30 * (1 - (maxPct - 50) / 50));

  // Factor 3 — Billing Commitment (0-20)
  const commitScore =
    status === "active"   && !subscription.cancelAtPeriodEnd ? 20 :
    status === "active"   &&  subscription.cancelAtPeriodEnd ? 10 :
    status === "trialing" ?  8 : 0;

  // Factor 4 — Plan Tier (0-10)
  const tierScore = plan === "enterprise" ? 10 : plan === "growth" ? 8 : plan === "starter" ? 6 : 3;

  const total = paymentScore + usageScore + commitScore + tierScore;

  const label =
    total >= 90 ? "Excellent" :
    total >= 75 ? "Good"      :
    total >= 60 ? "Fair"      :
    total >= 40 ? "At Risk"   : "Critical";

  const { ringColor, textColor, bgColor } =
    total >= 90 ? { ringColor: "#22c55e", textColor: "text-green-500",  bgColor: "bg-green-500/10"  } :
    total >= 75 ? { ringColor: "#4ade80", textColor: "text-emerald-500",bgColor: "bg-emerald-500/10"} :
    total >= 60 ? { ringColor: "#facc15", textColor: "text-yellow-500", bgColor: "bg-yellow-500/10" } :
    total >= 40 ? { ringColor: "#f97316", textColor: "text-orange-500", bgColor: "bg-orange-500/10" } :
                  { ringColor: "#ef4444", textColor: "text-red-500",    bgColor: "bg-red-500/10"    };

  const factors: ScoreFactor[] = [
    {
      name: "Payment Health",
      score: paymentScore,
      max: 40,
      description:
        status === "active"   ? "Subscription is active and in good standing" :
        status === "trialing" ? "Currently in trial period"                   :
        status === "past_due" ? "Payment has failed — update card now"        :
        plan !== "free"       ? "No active subscription found"                :
                                "Free plan — no billing required",
      color: paymentScore >= 32 ? "#22c55e" : paymentScore >= 16 ? "#f97316" : "#ef4444",
    },
    {
      name: "Usage Headroom",
      score: usageScore,
      max: 30,
      description:
        maxPct <= 50  ? "All usage metrics are well within limits"         :
        maxPct <= 80  ? "Usage is moderate — watch for approaching limits" :
        maxPct < 100  ? "One or more metrics are near their limit"         :
                        "Limit reached — upgrade to continue",
      color: usageScore >= 24 ? "#22c55e" : usageScore >= 12 ? "#f97316" : "#ef4444",
    },
    {
      name: "Billing Commitment",
      score: commitScore,
      max: 20,
      description:
        commitScore === 20 ? "Active subscription with auto-renewal enabled"    :
        commitScore === 10 ? "Subscription set to cancel at period end"          :
        commitScore ===  8 ? "Trial active — convert before trial ends"          :
                             "No active billing commitment",
      color: commitScore >= 16 ? "#22c55e" : commitScore >= 8 ? "#f97316" : "#ef4444",
    },
    {
      name: "Plan Tier",
      score: tierScore,
      max: 10,
      description:
        plan === "enterprise" ? "Enterprise plan: full feature access"        :
        plan === "growth"     ? "Growth plan: governance and assurance included" :
        plan === "starter"    ? "Starter plan: full scanning stack"            :
                                "Community plan: core engines, limited features",
      color: tierScore === 10 ? "#22c55e" : tierScore >= 6 ? "#1E4D38" : "#94a3b8",
    },
  ];

  return { total, label, ringColor, textColor, bgColor, factors };
}

// ── Animated ring (canvas-free SVG approach) ──────────────────────────────────

function AnimatedRing({
  score,
  color,
  size = 140,
  stroke = 10,
}: {
  score: number;
  color: string;
  size?: number;
  stroke?: number;
}) {
  const circleRef = useRef<SVGCircleElement>(null);
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const center = size / 2;

  useEffect(() => {
    if (!circleRef.current) return;
    // Animate from 0 to target on mount
    const target = (score / 100) * circ;
    const offset = circ - target;
    circleRef.current.style.strokeDashoffset = String(circ); // start empty
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (circleRef.current) {
          circleRef.current.style.transition = "stroke-dashoffset 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)";
          circleRef.current.style.strokeDashoffset = String(offset);
        }
      }, 100);
    });
  }, [score, circ]);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      {/* Track */}
      <circle
        cx={center} cy={center} r={radius}
        fill="none" stroke="currentColor" strokeWidth={stroke}
        className="text-secondary"
      />
      {/* Glow layer (slightly wider, lower opacity) */}
      <circle
        cx={center} cy={center} r={radius}
        fill="none" stroke={color} strokeWidth={stroke + 4} strokeOpacity={0.15}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={circ - (score / 100) * circ}
        style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.34,1.56,0.64,1) 0.05s" }}
      />
      {/* Progress */}
      <circle
        ref={circleRef}
        cx={center} cy={center} r={radius}
        fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={circ} // initial — animated via useEffect
      />
    </svg>
  );
}

// ── Factor mini-bar ───────────────────────────────────────────────────────────

function FactorBar({ factor }: { factor: ScoreFactor }) {
  const pct = Math.round((factor.score / factor.max) * 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-foreground">{factor.name}</span>
        <span className="text-xs font-mono text-foreground">
          {factor.score}<span className="text-muted-foreground">/{factor.max}</span>
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, backgroundColor: factor.color }}
        />
      </div>
      <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{factor.description}</p>
    </div>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

interface HealthScoreProps {
  overview: SubscriptionOverview;
  className?: string;
}

export default function HealthScore({ overview, className }: HealthScoreProps) {
  const { total, label, ringColor, textColor, bgColor, factors } = computeHealth(overview);

  return (
    <div className={cn("rounded-xl border border-border/60 bg-surface1/80 overflow-hidden", className)}>
      {/* Top gradient bar */}
      <div className="h-0.5 w-full" style={{ background: `linear-gradient(to right, ${ringColor}88, ${ringColor})` }} />

      <div className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm font-semibold text-foreground">Subscription Health</span>
          <span
            className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide", textColor, bgColor)}
          >
            {label}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Dial */}
          <div className="relative flex-shrink-0">
            <AnimatedRing score={total} color={ringColor} />
            {/* Centre overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn("text-3xl font-extrabold leading-none tabular-nums", textColor)}>
                {total}
              </span>
              <span className="text-xs text-muted-foreground leading-none mt-1">/ 100</span>
            </div>
          </div>

          {/* Factor breakdown */}
          <div className="flex-1 w-full space-y-3">
            {factors.map((f) => (
              <FactorBar key={f.name} factor={f} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
